import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Layout from './components/layout/Layout';

// Public Pages
import Home from './pages/public/Home';
import Facility from './pages/public/Facility';
import Shop from './pages/public/Shop';
import About from './pages/public/About';
import Contact from './pages/public/Contact';
import Login from './pages/public/Login';
import Register from './pages/public/Register';
import Pricing from './pages/public/Pricing';
import Cart from './pages/public/Cart';
import Checkout from './pages/public/Checkout';
import PaymentConfirmation from './pages/public/PaymentConfirmation';
import Wishlist from './pages/public/Wishlist';
import Activities from './pages/public/Activities';
import Blog from './pages/public/Blog';
import QuickStart from './pages/public/QuickStart';

// Service Pages (for the 6 services)
import GymService from './pages/services/GymService';
import SaunaService from './pages/services/SaunaService';
import SteamService from './pages/services/SteamService';
import YogaService from './pages/services/YogaService';
import TherapyService from './pages/services/TherapyService';
import NutritionService from './pages/services/NutritionService';

// User Pages
import Dashboard from './pages/user/Dashboard';
import Profile from './pages/user/Profile';
import Membership from './pages/user/Membership';
import Wallet from './pages/user/Wallet';
import Payments from './pages/user/Payments';
import Orders from './pages/user/Orders';
import Visits from './pages/user/Visits';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import GuestManagement from './pages/admin/GuestManagement';
import ActivityManagement from './pages/admin/ActivityManagement';
import ShopManagement from './pages/admin/ShopManagement';
import ProductManagement from './pages/admin/ProductManagement';
import InventoryManagement from './pages/admin/InventoryManagement';
import OrderProcessing from './pages/admin/OrderProcessing';
import Reports from './pages/admin/Reports';
import SaleManagement from './pages/admin/SaleManagement';
import BlogManagement from './pages/admin/BlogManagement'; // Add Blog Management import

// Super Admin Pages
import SuperAdminDashboard from './pages/superadmin/SuperAdminDashboard';
import UserManagement from './pages/superadmin/UserManagement';
import PackageConfig from './pages/superadmin/PackageConfig';
import SystemSettings from './pages/superadmin/SystemSettings';
import Analytics from './pages/superadmin/Analytics';
import AuditLogs from './pages/superadmin/AuditLogs';

// ─── SCROLL TO TOP ON ROUTE CHANGE ────────────────────────────────────────────
// React Router does not reset scroll position on navigation by default, so we
// do it manually here. Skips the reset when navigating to an in-page anchor
// (e.g. /pricing#plans) so hash links still work as expected.
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
};

function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Layout />}>
          {/* Public Routes */}
          <Route index element={<Home />} />
          <Route path="facility" element={<Facility />} />
          <Route path="shop" element={<Shop />} />
          <Route path="wishlist" element={<Wishlist />} />
          <Route path="about" element={<About />} />
          <Route path="contact" element={<Contact />} />
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="pricing" element={<Pricing />} />
          <Route path="cart" element={<Cart />} />
          <Route path="checkout" element={<Checkout />} />
          <Route path="payment-confirmation" element={<PaymentConfirmation />} />
          <Route path="activities" element={<Activities />} />
          <Route path="blog" element={<Blog />} />
          <Route path="quick-start" element={<QuickStart />} />

          {/* Service Routes - Individual service pages */}
          <Route path="services/gym" element={<GymService />} />
          <Route path="services/sauna" element={<SaunaService />} />
          <Route path="services/steam" element={<SteamService />} />
          <Route path="services/yoga" element={<YogaService />} />
          <Route path="services/therapy" element={<TherapyService />} />
          <Route path="services/nutrition" element={<NutritionService />} />

          {/* User Routes */}
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="profile" element={<Profile />} />
          <Route path="membership" element={<Membership />} />
          <Route path="wallet" element={<Wallet />} />
          <Route path="payments" element={<Payments />} />
          <Route path="orders" element={<Orders />} />
          <Route path="visits" element={<Visits />} />

          {/* Admin Routes */}
          <Route path="admin" element={<AdminDashboard />} />
          <Route path="admin/guests" element={<GuestManagement />} />
          <Route path="admin/activities" element={<ActivityManagement />} />
          <Route path="admin/products" element={<ProductManagement />} />
          <Route path="admin/inventory" element={<InventoryManagement />} />
          <Route path="admin/orders" element={<OrderProcessing />} />
          <Route path="admin/sale" element={<SaleManagement />} />
          <Route path="admin/blog" element={<BlogManagement />} />
          <Route path="admin/reports" element={<Reports />} />

          {/* Keep this if you still want the shop management overview */}
          <Route path="admin/shop" element={<ShopManagement />} />
          <Route path="admin/shop/products" element={<ProductManagement />} />
          <Route path="admin/shop/inventory" element={<InventoryManagement />} />
          <Route path="admin/shop/orders" element={<OrderProcessing />} />

          {/* Super Admin Routes */}
          <Route path="superadmin" element={<SuperAdminDashboard />} />
          <Route path="superadmin/users" element={<UserManagement />} />
          <Route path="superadmin/packages" element={<PackageConfig />} />
          <Route path="superadmin/settings" element={<SystemSettings />} />
          <Route path="superadmin/analytics" element={<Analytics />} />
          <Route path="superadmin/logs" element={<AuditLogs />} />
        </Route>
      </Routes>
    </>
  );
}

export default App;